import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { toast } from 'sonner';

const API = process.env.REACT_APP_BACKEND_URL;

const prompts = [
  { id: 1, text: "Look at yourself. Who are you right now?" },
  { id: 2, text: "What story have you been telling yourself?" },
  { id: 3, text: "What do you actually want?" },
  { id: 4, text: "What are you afraid to let go of?" },
  { id: 5, text: "If you could say one thing to yourself right now, what would it be?" },
];

export default function MirrorPage() {
  const { user, token } = useAuth();
  const navigate = useNavigate();

  const [phase, setPhase] = useState('intro'); // intro, video, reflect, affirm, complete
  const [currentPrompt, setCurrentPrompt] = useState(0);
  const [responses, setResponses] = useState({});
  const [affirmation, setAffirmation] = useState('');
  const [saving, setSaving] = useState(false);
  const [pastSessions, setPastSessions] = useState([]);
  const [showPast, setShowPast] = useState(false);

  useEffect(() => {
    if (!token) { navigate('/login'); return; }
    loadSessions();
  }, [token]);

  const headers = { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` };

  const loadSessions = async () => {
    try {
      const res = await fetch(`${API}/api/profile/mirror-sessions`, { headers });
      if (res.ok) setPastSessions(await res.json());
    } catch { toast.error('Could not load past sessions'); }
  };

  const handleResponse = (text) => {
    setResponses({ ...responses, [currentPrompt]: text });
  };

  const nextPrompt = () => {
    if (currentPrompt < prompts.length - 1) {
      setCurrentPrompt(currentPrompt + 1);
    } else {
      setPhase('affirm');
    }
  };

  const saveSession = async () => {
    setSaving(true);
    try {
      const reflections = prompts.map((p, i) => ({
        prompt: p.text,
        response: responses[i] || '',
      }));
      await fetch(`${API}/api/profile/mirror-sessions`, {
        method: 'POST',
        headers,
        body: JSON.stringify({ reflections, affirmation }),
      });
      setPhase('complete');
      loadSessions();
    } catch { toast.error('Could not save session. Please try again.'); }
    setSaving(false);
  };

  if (!user) return null;

  // Past sessions view
  if (showPast) {
    return (
      <section className="mirror-page" data-testid="mirror-past">
        <div className="mirror-past">
          <button className="mirror-back" onClick={() => setShowPast(false)}>
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><polyline points="15 18 9 12 15 6" /></svg>
            Back
          </button>
          <h1 className="mirror-past-title">Your Mirror Sessions</h1>
          {pastSessions.length === 0 && <p className="empty-state">No sessions yet. Enter The Mirror to begin.</p>}
          {pastSessions.map((session, si) => (
            <div key={si} className="mirror-session-card" data-testid={`session-${si}`}>
              <span className="mirror-session-date">{new Date(session.created_at).toLocaleDateString()}</span>
              {session.reflections?.map((r, ri) => (
                <div key={ri} className="mirror-session-qa">
                  <p className="mirror-session-prompt">{r.prompt}</p>
                  <p className="mirror-session-response">{r.response}</p>
                </div>
              ))}
              {session.affirmation && (
                <p className="mirror-session-affirmation">"{session.affirmation}"</p>
              )}
            </div>
          ))}
        </div>
      </section>
    );
  }

  return (
    <section className="mirror-page" data-testid="mirror-page">

      {/* Phase: Intro */}
      {phase === 'intro' && (
        <div className="mirror-intro" data-testid="mirror-intro">
          <div className="mirror-glow" />
          <h1 className="mirror-title">The Mirror</h1>
          <p className="mirror-subtitle">This is where you look inside your soul.</p>
          <p className="mirror-description">
            Take a breath. Be still. What you are about to do is between you and...<br />
            The God in YOU.
          </p>
          <div className="mirror-intro-actions">
            <button
              data-testid="begin-mirror-btn"
              className="village-btn village-btn--primary"
              onClick={() => setPhase('video')}
            >
              Enter The Mirror
            </button>
            {pastSessions.length > 0 && (
              <button
                data-testid="past-sessions-btn"
                className="village-btn village-btn--secondary"
                onClick={() => setShowPast(true)}
              >
                Past Sessions ({pastSessions.length})
              </button>
            )}
          </div>
        </div>
      )}

      {/* Phase: Meditative Video */}
      {phase === 'video' && (
        <div className="mirror-video-phase" data-testid="mirror-video">
          <div className="mirror-video-container">
            <div className="video-placeholder">
              <div className="video-inner">
                <div className="play-icon">
                  <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
                    <polygon points="5 3 19 12 5 21 5 3" fill="currentColor" />
                  </svg>
                </div>
                <p className="video-label">Guided Mirror Meditation by Cabrini</p>
                <p className="video-sublabel">Video coming soon</p>
              </div>
            </div>
          </div>
          <button
            data-testid="start-reflection-btn"
            className="village-btn village-btn--primary"
            onClick={() => setPhase('reflect')}
          >
            Begin Your Reflection
          </button>
        </div>
      )}

      {/* Phase: Reflection Prompts */}
      {phase === 'reflect' && (
        <div className="mirror-reflect" data-testid="mirror-reflect">
          <div className="mirror-progress">
            <div className="mirror-progress-bar">
              <div className="mirror-progress-fill" style={{ width: `${((currentPrompt + 1) / prompts.length) * 100}%` }} />
            </div>
            <span className="mirror-progress-text">{currentPrompt + 1} of {prompts.length}</span>
          </div>
          <div className="mirror-prompt-card" key={prompts[currentPrompt].id}>
            <p className="mirror-prompt-text">{prompts[currentPrompt].text}</p>
            <textarea
              data-testid="mirror-response-input"
              className="mirror-textarea"
              placeholder="Write what comes to you..."
              value={responses[currentPrompt] || ''}
              onChange={(e) => handleResponse(e.target.value)}
              rows={5}
            />
            <button
              data-testid="mirror-next-btn"
              className="village-btn village-btn--primary"
              onClick={nextPrompt}
            >
              {currentPrompt < prompts.length - 1 ? 'Next' : 'Create Your Affirmation'}
            </button>
          </div>
        </div>
      )}

      {/* Phase: Affirmation */}
      {phase === 'affirm' && (
        <div className="mirror-affirm" data-testid="mirror-affirm">
          <h2 className="mirror-affirm-title">Now speak your truth.</h2>
          <p className="mirror-affirm-subtitle">
            Write one affirmation — a declaration of who you are becoming.
          </p>
          <textarea
            data-testid="affirmation-input"
            className="mirror-textarea mirror-textarea--affirm"
            placeholder="I am..."
            value={affirmation}
            onChange={(e) => setAffirmation(e.target.value)}
            rows={3}
          />
          <button
            data-testid="save-session-btn"
            className="village-btn village-btn--primary"
            onClick={saveSession}
            disabled={saving}
          >
            {saving ? 'Saving...' : 'Seal This Session'}
          </button>
        </div>
      )}

      {/* Phase: Complete */}
      {phase === 'complete' && (
        <div className="mirror-complete" data-testid="mirror-complete">
          <div className="mirror-glow" />
          <h2 className="mirror-complete-title">The Mirror remembers.</h2>
          {affirmation && <p className="mirror-complete-affirmation">"{affirmation}"</p>}
          <p className="mirror-complete-text">
            This session is saved. Come back anytime — your transformation is a journey, not a moment.
          </p>
          <div className="mirror-complete-actions">
            <button className="village-btn village-btn--primary" onClick={() => navigate('/profile')}>
              Back to Profile
            </button>
            <button className="village-btn village-btn--secondary" onClick={() => navigate('/life-map')}>
              Return to Village
            </button>
          </div>
        </div>
      )}
    </section>
  );
}
